import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import PageWrapper from '../components/PageWrapper';
import '../styles/PapabiliPage.css';

// Composants SVG inline
const IconCrown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
  </svg>
);

const IconChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const IconChevronUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

const IconArrowUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"></line>
    <polyline points="5 12 12 5 19 12"></polyline>
  </svg>
);

const PapabiliPage = () => {
  const [papabili, setPapabili] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [expandDetails, setExpandDetails] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  
  // Image de fallback pour les cardinaux sans photo
  const defaultImage = '/images/cardinal-default.png';
  
  useEffect(() => {
    const fetchPapabili = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/cardinaux/search?papabile=true');
        const papabiliData = response.data.cardinaux;
        
        // Trier les papabili par un score estimé basé sur divers facteurs
        const sortedPapabili = [...papabiliData].sort((a, b) => {
          // Facteurs de tri: âge (les plus jeunes d'abord), puis électeur ou non, puis ordre alphabétique
          if (a.statutVotant === 'OUI' && b.statutVotant !== 'OUI') return -1;
          if (a.statutVotant !== 'OUI' && b.statutVotant === 'OUI') return 1;
          return a.age - b.age || a.nomComplet.localeCompare(b.nomComplet);
        });
        
        setPapabili(sortedPapabili);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des papabili:', err);
        setError('Erreur lors du chargement des papabili');
        setLoading(false);
      }
    };
    
    fetchPapabili();
    
    // Gestionnaire pour le bouton "retour en haut"
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Nettoyage
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, cardinalId) => {
    if (!originalUrl || imageErrors[cardinalId]) return defaultImage;
    
    // Extraire le nom du fichier de l'URL
    const filename = originalUrl.split('/').pop().split('?')[0];
    
    // Ajouter un timestamp pour éviter le cache du navigateur
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (cardinalId) => {
    console.log(`Erreur de chargement d'image pour cardinal ${cardinalId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [cardinalId]: true
    }));
  };
  
  // Déplier/replier les détails d'un cardinal
  const toggleDetails = (id) => {
    setExpandDetails((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Remonter en haut de la page
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <PageWrapper pageType="papabili">
      <div className="papabili-page">
        <div className="papabili-header">
          <h1>Les Papabili</h1>
          <p className="subtitle">
            Découvrez les cardinaux considérés comme "papabile", c'est-à-dire ayant 
            le potentiel d'être élus pape lors du prochain conclave
          </p>
        </div>

        <div className="papabili-content-with-sidebar">
          <div className="papabili-main-content">

            <div className="papabili-current">
              <h2>
                <IconCrown />
                Les cardinaux papabili actuels
              </h2>
              
              <p className="listing-explanation">
                Les cardinaux ci-dessous sont considérés comme papabili selon diverses sources: 
                experts vaticanistes, observateurs de l'Église, et analyses médiatiques. Ce classement 
                n'est pas officiel et reflète uniquement des opinions extérieures au Sacré Collège.
              </p>
              
              <div className="papabili-list">
                {loading ? (
                  <div className="loading">Chargement des cardinaux papabili...</div>
                ) : error ? (
                  <div className="error">{error}</div>
                ) : papabili.length === 0 ? (
                  <div className="no-results">Aucun cardinal papabile n'a été trouvé</div>
                ) : (
                  papabili.map((cardinal) => (
                    <div key={cardinal._id} className="papabile-card">
                      <div className="papabile-main">
                        <div className="papabile-photo">
                          <img 
                            src={getApiImageUrl(cardinal.photo, cardinal._id)} 
                            alt={cardinal.nomComplet} 
                            onError={() => handleImageError(cardinal._id)}
                          />
                          
                          {cardinal.statutVotant === 'OUI' && (
                            <div className="elector-badge" title="Électeur au conclave">
                              <IconCrown />
                            </div>
                          )}
                        </div>
                        
                        <div className="papabile-info">
                          <h3>{cardinal.nomComplet}</h3>
                          
                          <div className="papabile-meta">
                            <span className="info-item">{cardinal.age} ans</span>
                            <span className="info-item">{cardinal.paysService}</span>
                            <span className="info-item">{cardinal.continent}</span>
                          </div>
                          
                          <p className="papabile-function">
                            {cardinal.titre || cardinal.ordre}
                          </p>
						<Link to={`/cardinaux/${cardinal.slug}`} className="profile-link_basic">
                            >> Voir le profil complet
                          </Link>
                        </div>
                        
                        <button 
                          className="expand-button"
                          onClick={() => toggleDetails(cardinal._id)}
                          aria-expanded={expandDetails[cardinal._id]}
                          aria-label={expandDetails[cardinal._id] ? "Masquer les détails" : "Afficher les détails"}
                        >
                          {expandDetails[cardinal._id] ? <IconChevronUp /> : <IconChevronDown />}
                        </button>
                      </div>
                      
                      {expandDetails[cardinal._id] && (
                        <div className="papabile-details">
                          <div className="details-columns">
                            <div>
                              <div className="details-section">
                                <h4>Atouts comme papabile</h4>
                                {cardinal.raisonsForPapabile && cardinal.raisonsForPapabile.length > 0 ? (
                                  <ul>
                                    {Array.isArray(cardinal.raisonsForPapabile) ? (
                                      cardinal.raisonsForPapabile.map((raison, index) => (
                                        <li key={index}>{raison}</li>
                                      ))
                                    ) : (
                                      <li>{cardinal.raisonsForPapabile}</li>
                                    )}
                                  </ul>
                                ) : (
                                  <p>Information non disponible</p>
                                )}
                              </div>
                            </div>
                            
                            <div>
                              <div className="details-section">
                                <h4>Facteurs limitants</h4>
                                {cardinal.raisonsAgainstPapabile && cardinal.raisonsAgainstPapabile.length > 0 ? (
                                  <ul>
                                    {Array.isArray(cardinal.raisonsAgainstPapabile) ? (
                                      cardinal.raisonsAgainstPapabile.map((raison, index) => (
                                        <li key={index}>{raison}</li>
                                      ))
                                    ) : (
                                      <li>{cardinal.raisonsAgainstPapabile}</li>
                                    )}
                                  </ul>
                                ) : (
                                  <p>Information non disponible</p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="details-section">
                            <h4>Informations complémentaires</h4>
                            <p>
                              <strong>Date de naissance:</strong> {formatDate(cardinal.dateNaissance)} | 
                              <strong> Créé cardinal par:</strong> {cardinal.creePar || "Information non disponible"} |
                              <strong> Date de création:</strong> {formatDate(cardinal.dateCreation)}
                            </p>
                            {cardinal.biographie && (
                              <p><strong>Biographie:</strong> {cardinal.biographie.substring(0, 200)}
                                {cardinal.biographie.length > 200 ? '...' : ''}
                              </p>
                            )}
                          </div>
                          
                          <Link to={`/cardinaux/${cardinal.slug}`} className="profile-link">
                            Voir le profil complet
                          </Link>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
			            <div className="papabili-explanation">
              <div className="explanation-content">
                <h2>Qu'est-ce qu'un "papabile" ?</h2>
                <p>
                  Le terme "papabile" (pluriel: "papabili") désigne un cardinal qui est considéré comme
                  ayant une chance raisonnable d'être élu pape lors d'un conclave. Ce concept italien,
                  qui signifie littéralement "papable" ou "éligible au poste de pape", est utilisé par
                  les observateurs du Vatican, les médias et les experts pour identifier les cardinaux
                  qui pourraient potentiellement accéder au trône pontifical.
                </p>
                <p>
                  Les cardinaux désignés comme papabili présentent généralement plusieurs atouts:
                  une réputation de piété et d'intégrité, une expérience administrative significative,
                  une vision théologique solide, et souvent une aptitude à communiquer en plusieurs langues.
                  L'âge joue également un rôle important - les cardinaux entre 65 et 75 ans sont souvent
                  considérés comme ayant l'âge idéal.
                </p>
                <ul>
                  <li>Un adage populaire dit: "Qui entre pape au conclave en sort cardinal" - suggérant que
                  les favoris ne sont pas toujours élus.</li>
                  <li>Lors d'un conclave, des dynamiques complexes peuvent émerger, et parfois un
                  "candidat de compromis" moins attendu peut être choisi.</li>
                  <li>Les cardinaux eux-mêmes sont souvent réticents à être désignés comme papabili,
                  par humilité ou pour éviter des attentes excessives.</li>
                </ul>
              </div>
              <div className="explanation-image">
                <img 
                  src="/images/sixtine-chapel.png" 
                  alt="Conclave papal à la Chapelle Sixtine" 
                />
                <span className="image-caption">Les cardinaux lors d'un conclave à la Chapelle Sixtine</span>
              </div>
            </div>
            	  
          </div>
          
          <div className="papabili-sidebar">
            <div className="sidebar-content">
		  		<h3>Les Papabili</h3>
				<ul>
				  <li>
					<a href="/papabile-du-jour" className="sidebar-highlight-link">
					  <div className="sidebar-highlight-icon">
						<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						  <circle cx="12" cy="12" r="5" />
						  <line x1="12" y1="1" x2="12" y2="3" />
						  <line x1="12" y1="21" x2="12" y2="23" />
						  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
						  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
						  <line x1="1" y1="12" x2="3" y2="12" />
						  <line x1="21" y1="12" x2="23" y2="12" />
						  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
						  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
						</svg>
					  </div>
					  <span>Papabile du Jour</span>
					</a>
				  </li>
				</ul>
		  
              <h3>Critères importants</h3>
              <ul>
                <li><a href="#age">Âge et santé</a></li>
                <li><a href="#experience">Expérience pastorale</a></li>
                <li><a href="#communications">Compétences en communication</a></li>
                <li><a href="#languages">Maîtrise des langues</a></li>
                <li><a href="#balance">Équilibre théologique</a></li>
                <li><a href="#global">Vision mondiale de l'Église</a></li>
              </ul>
              

			  
              <h3>En savoir plus</h3>
              <div className="sidebar-info">
                <p>
                  Le processus de sélection d'un pape est complexe et influencé par de nombreux facteurs
                  allant de la théologie à la géopolitique. Les électeurs considèrent autant les qualités
                  personnelles que la direction que le candidat pourrait donner à l'Église.
                </p>
                <p>
                  Explorez notre <a href="/conclave">simulation de conclave</a> pour participer
                  à un vote virtuel et voir quels cardinaux recueillent le plus de soutien parmi
                  les utilisateurs du site.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {showBackToTop && (
          <button 
            className="back-to-top" 
            onClick={scrollToTop} 
            aria-label="Retour en haut de la page"
          >
            <IconArrowUp />
          </button>
        )}
      </div>
    </PageWrapper>
  );
};

export default PapabiliPage;